// src/components/Terminal.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Bot,
    Activity,
    Terminal as TerminalIcon,
    Braces,
    Waypoints,
    Binary,
    Network,
    Fan,
    Brain,
    Rocket,
    Clock,
    Timer,
    Loader2
} from 'lucide-react';

// ---------------------------------------------------------------------
// Utility Function: formatESTTime
// ---------------------------------------------------------------------
const formatESTTime = (isoString) => {
    const options = {
        timeZone: 'America/New_York',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    };
    const formatter = new Intl.DateTimeFormat('en-US', options);
    return `${formatter.format(new Date(isoString))} EST`;
};

// ---------------------------------------------------------------------
// CompletionSummary: Moved Outside Terminal
// ---------------------------------------------------------------------
const CompletionSummary = React.memo(({ summary, tokenName, tokenWebsite, tokenLogo, tokenId, pumpfunUrl }) => {
    if (!summary) return null;

    // Replace spaces in the tokenWebsite subdomain with "-"
    const sanitizedTokenWebsite = tokenWebsite
        ? tokenWebsite.replace(/\s+/g, '-')
        : null;

    // Build the logo URL based on the environment
    const isDev = process.env.NODE_ENV === 'development';
    const logoUrl = tokenLogo
        ? `${isDev ? 'http://localhost:3000' : ''}/api/configs/image/${tokenLogo}`
        : `${process.env.PUBLIC_URL}/kyora.png`; // Updated fallback path

    return (
        <div className="mt-6 mb-8 bg-gradient-to-r from-blue-900/20 via-indigo-900/20 to-blue-900/20 rounded-lg border border-blue-500/30 p-6">
            <div className="flex items-center gap-3 mb-4">
                <div className="relative">
                    <div className="absolute -inset-2 bg-blue-500/20 rounded-full blur-lg animate-pulse" />
                    <img
                        src={logoUrl}
                        alt={tokenName}
                        className="relative w-12 h-12 rounded-full border-2 border-blue-400"
                    />
                </div>
                <div>
                    <h3 className="text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-400">
                        {tokenName} - Token Creation Summary
                    </h3>
                    <p className="text-blue-200/70 text-sm">
                        Process Analysis & Market Intelligence
                    </p>
                </div>
            </div>

            <div className="space-y-4">
                <div className="bg-blue-950/30 rounded-lg p-4 border border-blue-500/20">
                    <div className="font-mono text-blue-200 whitespace-pre-wrap">
                        {summary}
                        {/* Add Kyora logo and text */}
                        <div className="flex items-center gap-2 mt-4">
                            <img
                                src={process.env.PUBLIC_URL + '/kyora.png'}
                                alt="Kyora"
                                className="w-6 h-6 rounded-full border border-blue-400/50"
                            />
                            <span className="text-blue-200/70">- Kyora AI</span>
                        </div>
                    </div>
                </div>

                {/* Add links vertically */}
                <div className="flex flex-col gap-2 mt-4">
                    {pumpfunUrl && (
                        <a
                            href={pumpfunUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-400 hover:text-blue-300 underline"
                        >
                            Visit PumpFun Link
                        </a>
                    )}
                    {sanitizedTokenWebsite && (
                        <a
                            href={sanitizedTokenWebsite}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-400 hover:text-blue-300 underline"
                        >
                            Visit Token Website
                        </a>
                    )}
                    {tokenId && (
                        <a
                            href={`/token-history/${tokenId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-400 hover:text-blue-300 underline"
                        >
                            View Token History
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
});

// ---------------------------------------------------------------------
// MessageComponent: Moved Outside Terminal
// ---------------------------------------------------------------------
const MessageComponent = React.memo(({ message, showApiOnly = false, pumpfunUrl, formatESTTime }) => {
    // Icon based on message type
    const getIcon = () => {
        const icons = {
            AI_RESPONSE: Brain,
            COMMAND: Rocket,
            SYSTEM: Brain,
            WARNING: Activity,
            ERROR: Activity,
            LOG: Brain,
            API_RESPONSE: Activity,
            COMPLETION: Brain // Add COMPLETION type
        };
        const IconComponent = icons[message.type] || Brain;
        return <IconComponent className="w-4 h-4 text-blue-400" />;
    };

    // Decide if this message shows in main terminal or API terminal
    const shouldShow = () => {
        if (showApiOnly) {
            // Show only API responses in API terminal
            return message.type === 'API_RESPONSE';
        } else {
            // Main terminal shows everything except API responses
            return message.type !== 'API_RESPONSE';
        }
    };

    // Handle COMPLETION messages
    if (message.type === 'COMPLETION') {
        const { result_thought, next_thought, creation_summary, tokenName, tokenWebsite, tokenLogo, tokenId } = message.content || {};

        // Use pumpfunUrl from props
        return (
            <div className="mb-4 leading-relaxed break-words">
                <div className="flex items-center gap-2 text-blue-200/70 mb-1">
                    <span>[{formatESTTime(message.timestamp)}]</span>
                    <Brain className="w-4 h-4 text-blue-400" />
                </div>

                {/* Display result_thought if it exists */}
                {result_thought && (
                    <div className="flex items-start gap-3 bg-blue-950/30 rounded-lg p-3 mb-3">
                        <div className="relative flex-shrink-0">
                            <img
                                src={process.env.PUBLIC_URL + '/kyora.png'}
                                alt="Kyora"
                                className="w-8 h-8 rounded-full border border-blue-400/50"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-blue-500/20 to-transparent rounded-full" />
                        </div>
                        <div className="text-blue-200">{result_thought}</div>
                    </div>
                )}

                {/* Display next_thought if it exists */}
                {next_thought && (
                    <div className="flex items-start gap-3 bg-emerald-950/30 rounded-lg p-3 mb-3">
                        <div className="relative flex-shrink-0">
                            <img
                                src={process.env.PUBLIC_URL + '/kyora.png'}
                                alt="Kyora"
                                className="w-8 h-8 rounded-full border border-emerald-400/50"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-emerald-500/20 to-transparent rounded-full" />
                        </div>
                        <div className="text-emerald-200">{next_thought}</div>
                    </div>
                )}

                {/* Display creation_summary if it exists */}
                {creation_summary && (
                    <CompletionSummary
                        summary={creation_summary}
                        tokenName={tokenName}
                        tokenWebsite={tokenWebsite}
                        tokenLogo={tokenLogo}
                        tokenId={tokenId}
                        pumpfunUrl={pumpfunUrl} // Pass the PumpFun URL
                    />
                )}
            </div>
        );
    }

    if (!shouldShow()) {
        return null;
    }

    // Style classes per message type
    const getMessageStyle = () => {
        const styles = {
            AI_RESPONSE: 'text-emerald-300 bg-emerald-900/20 rounded p-2 font-mono whitespace-pre',
            COMMAND: 'text-yellow-300 bg-yellow-900/20 rounded p-2 font-mono whitespace-pre animate-pulse',
            SYSTEM: 'text-blue-200 font-bold',
            WARNING: 'text-orange-300 font-bold',
            ERROR: 'text-red-400 font-bold',
            LOG: 'text-blue-200',
            API_REQUEST: 'text-yellow-300 bg-yellow-900/20 rounded p-2 font-mono whitespace-pre mb-1',
            API_RESPONSE: 'text-green-300 bg-green-900/20 rounded p-2 font-mono whitespace-pre'
        };
        return styles[message.type] || styles.LOG;
    };

    // Format AI_RESPONSE content
    const formatAIResponse = () => {
        // Try to parse content if it's a string containing JSON
        let thoughts = null;

        if (message.thought) {
            thoughts = {
                result: message.thought.result_thought,
                next: message.thought.next_thought,
                command: message.thought.command
            };
        } else if (typeof message.content === 'string') {
            try {
                const parsed = JSON.parse(message.content);
                thoughts = {
                    result: parsed.result_thought,
                    next: parsed.next_thought,
                    command: parsed.command
                };
            } catch (e) {
                // Not JSON, use as is
            }
        } else if (typeof message.message === 'string') {
            try {
                const parsed = JSON.parse(message.message);
                thoughts = {
                    result: parsed.result_thought,
                    next: parsed.next_thought,
                    command: parsed.command
                };
            } catch (e) {
                // Not JSON, use as is
            }
        }

        return (
            <div className="space-y-4">
                {thoughts && (
                    <>
                        {/* Main thought with Brain icon */}
                        {thoughts.result && (
                            <div className="flex items-start gap-3 bg-blue-950/30 rounded-lg p-3">
                                <div className="relative flex-shrink-0">
                                    <img
                                        src={process.env.PUBLIC_URL + '/kyora.png'}
                                        alt="Kyora"
                                        className="w-8 h-8 rounded-full border border-blue-400/50"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-blue-500/20 to-transparent rounded-full" />
                                </div>
                                <div className="text-blue-200">
                                    {thoughts.result}
                                </div>
                            </div>
                        )}

                        {/* Next thought with Brain icon */}
                        {thoughts.next && (
                            <div className="flex items-start gap-3 bg-emerald-950/30 rounded-lg p-3">
                                <div className="relative flex-shrink-0">
                                    <img
                                        src={process.env.PUBLIC_URL + '/kyora.png'}
                                        alt="Kyora"
                                        className="w-8 h-8 rounded-full border border-emerald-400/50"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-emerald-500/20 to-transparent rounded-full" />
                                </div>
                                <div className="text-emerald-200">
                                    {thoughts.next}
                                </div>
                            </div>
                        )}

                        {/* Command with Rocket icon - Updated with better overflow handling */}
                        {thoughts.command && (
                            <div className="flex items-start gap-3 bg-yellow-950/30 rounded-lg p-3">
                                <Rocket className="w-6 h-6 text-yellow-400 flex-shrink-0" />
                                <div className="flex-grow">
                                    <div className="text-yellow-200 mb-2">Executing command:</div>
                                    <div className="text-yellow-300 bg-yellow-900/20 rounded p-2 font-mono whitespace-pre-wrap break-all overflow-x-auto max-w-full">
                                        {typeof thoughts.command === 'string'
                                            ? thoughts.command
                                            : JSON.stringify(thoughts.command, null, 2)}
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };

    // Format API_RESPONSE content
    const formatAPIResponse = () => {
        if (message.type !== 'API_RESPONSE') return null;

        return (
            <div className="mb-6">
                {/* Display the associated request command */}
                {message.command && (
                    <>
                        <div className="text-blue-200 mb-1 font-semibold">Request:</div>
                        <div className="text-yellow-300 bg-yellow-900/20 rounded p-2 font-mono whitespace-pre mb-2">
                            {typeof message.command === 'string'
                                ? message.command
                                : JSON.stringify(message.command, null, 2)}
                        </div>
                    </>
                )}
                {/* Display the API response */}
                <div className="text-blue-200 mb-1 font-semibold">Response:</div>
                <div className="text-green-300 bg-green-900/20 rounded p-2 font-mono whitespace-pre">
                    {typeof message.content === 'string'
                        ? message.content
                        : JSON.stringify(message.content, null, 2)}
                </div>
            </div>
        );
    };

    return (
        <div className="mb-4 leading-relaxed break-words">
            {!showApiOnly && (
                <div className="flex items-center gap-2 text-blue-200/70 mb-1">
                    <span>[{formatESTTime(message.timestamp)}]</span>
                    {getIcon()}
                </div>
            )}
            {message.type === 'AI_RESPONSE' ? (
                formatAIResponse()
            ) : showApiOnly ? (
                formatAPIResponse()
            ) : (
                <div className={`${getMessageStyle()} max-w-full`}>
                    {typeof message.content === 'string'
                        ? message.content
                        : JSON.stringify(message.content, null, 2)}
                </div>
            )}
        </div>
    );
});

// ---------------------------------------------------------------------
// Terminal Component
// ---------------------------------------------------------------------
const Terminal = () => {
    const [messages, setMessages] = useState([]);
    const [connected, setConnected] = useState(false);
    const [currentTypingMessage, setCurrentTypingMessage] = useState(null);
    const [autoScroll, setAutoScroll] = useState(true);
    const warningDisplayed = useRef(false);
    const [systemStatus, setSystemStatus] = useState({
        robotStatus: 'IDLE',
        currentTask: '',
        progress: 0,
        isInitializing: false
    });
    const [nextRunTime, setNextRunTime] = useState(null);
    const [countdown, setCountdown] = useState(null);
    const [lastProcessEnd, setLastProcessEnd] = useState(null);
    const [isInitializing, setIsInitializing] = useState(false);
    const [processStatus, setProcessStatus] = useState('standby');

    // Remove formatESTTime from here since it's moved outside

    useEffect(() => {
        let countdownTimer;
        let initTimer;

        if (nextRunTime && processStatus !== 'creating') {
            const updateCountdown = () => {
                const now = new Date().getTime();
                const target = new Date(nextRunTime).getTime();
                const diff = target - now;

                if (diff <= 0) {
                    if (countdownTimer) clearInterval(countdownTimer);
                    setCountdown('Initializing...');
                } else {
                    const hours = Math.floor(diff / (1000 * 60 * 60));
                    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
                    setCountdown(`${hours}h ${minutes}m ${seconds}s`);
                }
            };

            updateCountdown();
            countdownTimer = setInterval(updateCountdown, 1000);
        }

        return () => {
            if (countdownTimer) clearInterval(countdownTimer);
            if (initTimer) clearTimeout(initTimer);
        };
    }, [nextRunTime, processStatus]);


    useEffect(() => {
        if (!autoScroll) return;  // Skip if auto-scroll is disabled

        const scrollToBottom = () => {
            if (terminalRef.current) {
                const scrollHeight = terminalRef.current.scrollHeight;
                const height = terminalRef.current.clientHeight;
                const maxScrollTop = scrollHeight - height;
                terminalRef.current.scrollTop = maxScrollTop > 0 ? maxScrollTop : 0;
            }
        };

        scrollToBottom();
        const timeoutId = setTimeout(scrollToBottom, 100);
        return () => clearTimeout(timeoutId);
    }, [messages, currentTypingMessage, autoScroll]);

    const webSocket = useRef(null);
    const terminalRef = useRef(null);

    // Tracks if this is the very first load (to optionally skip typed animation at page load)
    const isFirstLoad = useRef(true);

    // Typing animation speed (milliseconds between characters)
    const typingSpeed = 5;

    // A queue that stores incoming messages for sequential processing
    const messageQueue = useRef([]);

    // Flag to indicate we're currently typing a message (so we don't process next)
    const isProcessing = useRef(false);

    // Ref to store processed messages for deduplication
    const processedMessages = useRef(new Set());

    // Ref to store pending commands for associating with API responses
    const pendingCommands = useRef([]);

    // ---------------------------------------------------------------------
    // processQueue: Dequeue & process messages one at a time
    // ---------------------------------------------------------------------
    const processQueue = useCallback(async () => {
        // If we're mid-typing or no messages in queue, do nothing
        if (isProcessing.current) return;
        if (messageQueue.current.length === 0) return;

        isProcessing.current = true;
        const nextMessage = messageQueue.current.shift();

        try {
            await processMessage(nextMessage, isFirstLoad.current);
        } catch (err) {
            console.error('Error processing queued message:', err);
        }

        isProcessing.current = false;

        // After we finish, if there's more in the queue, process the next
        if (messageQueue.current.length > 0) {
            processQueue();
        }
    }, [/* no external deps */]);

    const AutoScrollToggle = React.memo(() => {
        return (
            <div className="flex items-center gap-2 px-2 py-1 bg-blue-950/30 rounded-lg border border-blue-500/30 hover:border-blue-400/50 transition-colors cursor-pointer">
                <div className={`relative w-6 h-3 rounded-full transition-colors ${autoScroll ? 'bg-blue-500' : 'bg-gray-600'}`}>
                    <div className={`absolute top-0.5 left-0.5 w-2 h-2 rounded-full transition-transform transform ${autoScroll ? 'translate-x-3 bg-white' : 'translate-x-0 bg-gray-300'}`} />
                </div>
                <span className="text-xs text-blue-200 font-mono">
                    {autoScroll ? 'ON' : 'OFF'}
                </span>
            </div>
        );
    });

    // ---------------------------------------------------------------------
    // formatJsonContent: If content has ```json code blocks, format them
    // ---------------------------------------------------------------------
    const formatJsonContent = (content) => {
        try {
            if (content.includes('```json')) {
                const jsonStr = content.replace(/```json\s*|\s*```/g, '');
                const parsed = JSON.parse(jsonStr);
                return JSON.stringify(parsed, null, 2);
            }
            return content;
        } catch (e) {
            return content;
        }
    };

    // ---------------------------------------------------------------------
    // processMessage: Typing animation (unless immediate=true or an API_RESPONSE)
    // ---------------------------------------------------------------------
    const processMessage = useCallback(async (message, immediate = false) => {
        console.log('Processing message:', message);

        if (message.type === 'API_RESPONSE') {
            let associatedCommand = null;
            if (pendingCommands.current.length > 0) {
                associatedCommand = pendingCommands.current.shift();
            }

            setMessages(prev => [
                ...prev,
                {
                    content: message.content,
                    type: 'API_RESPONSE',
                    command: associatedCommand,
                    timestamp: message.timestamp || new Date().toISOString(),
                    id: Math.random().toString(36).substr(2, 9),
                    showAfter: prev.length
                }
            ]);
            return;
        }

        // Handle COMPLETION messages
        if (message.type === 'COMPLETION') {
            setMessages(prev => [
                ...prev,
                {
                    ...message,
                    id: Math.random().toString(36).substr(2, 9)
                }
            ]);
            return;
        }

        // Handle AI_RESPONSE messages with typing animation
        if (message.type === 'AI_RESPONSE') {
            if (message.thought?.command) {
                pendingCommands.current.push(message.thought.command);
            }

            // Skip animation if immediate is true
            if (immediate) {
                setMessages(prev => [...prev, {
                    ...message,
                    id: Math.random().toString(36).substr(2, 9)
                }]);
                return;
            }

            // Create a temporary message for typing animation without command initially
            const typingMessage = {
                ...message,
                thought: {
                    ...message.thought,
                    result_thought: '',
                    next_thought: '',
                    command: null // Start with no command
                },
                id: Math.random().toString(36).substr(2, 9)
            };

            setCurrentTypingMessage(typingMessage);

            // Animate result_thought
            if (message.thought?.result_thought) {
                const chars = message.thought.result_thought.split('');
                for (const char of chars) {
                    await new Promise(resolve => setTimeout(resolve, typingSpeed));
                    setCurrentTypingMessage(prev => ({
                        ...prev,
                        thought: {
                            ...prev.thought,
                            result_thought: prev.thought.result_thought + char
                        }
                    }));
                }
            }

            // Animate next_thought
            if (message.thought?.next_thought) {
                await new Promise(resolve => setTimeout(resolve, typingSpeed * 2)); // Small pause between thoughts
                const chars = message.thought.next_thought.split('');
                for (const char of chars) {
                    await new Promise(resolve => setTimeout(resolve, typingSpeed));
                    setCurrentTypingMessage(prev => ({
                        ...prev,
                        thought: {
                            ...prev.thought,
                            next_thought: prev.thought.next_thought + char
                        }
                    }));
                }
            }

            // Small pause before showing command
            if (message.thought?.command) {
                await new Promise(resolve => setTimeout(resolve, typingSpeed * 4));
                setCurrentTypingMessage(prev => ({
                    ...prev,
                    thought: {
                        ...prev.thought,
                        command: message.thought.command
                    }
                }));
            }

            // Small pause before final message
            await new Promise(resolve => setTimeout(resolve, typingSpeed * 2));

            // Final message
            setMessages(prev => [...prev, {
                ...message,
                id: Math.random().toString(36).substr(2, 9)
            }]);
            setCurrentTypingMessage(null);
            return;
        }

        // Handle other message types as before...
        const finalType = message.type || 'text';
        const newMessage = {
            content: message.message,
            type: finalType,
            timestamp: message.timestamp || new Date().toISOString(),
            id: Math.random().toString(36).substr(2, 9)
        };

        if (immediate) {
            setMessages(prev => [...prev, newMessage]);
            return;
        }

        setCurrentTypingMessage(newMessage);

        if (typeof newMessage.content === 'string') {
            let currentText = '';
            const chars = newMessage.content.split('');

            for (const char of chars) {
                currentText += char;
                setCurrentTypingMessage(prev => ({
                    ...prev,
                    content: currentText
                }));
                await new Promise(resolve => setTimeout(resolve, typingSpeed));
            }
        }

        setMessages(prev => [...prev, { ...newMessage }]);
        setCurrentTypingMessage(null);
    }, [/* typingSpeed */]);

    // ---------------------------------------------------------------------
    // handleWebSocketMessage: On incoming WS data, queue it and processQueue
    // ---------------------------------------------------------------------

    const CountdownDisplay = React.memo(() => {
        const [dots, setDots] = useState('');

        useEffect(() => {
            let dotTimer;
            if (processStatus === 'creating') {
                dotTimer = setInterval(() => {
                    setDots(prev => prev.length >= 3 ? '' : prev + '.');
                }, 500);
            }
            return () => clearInterval(dotTimer);
        }, [processStatus]);

        if (!nextRunTime && processStatus !== 'creating') return null;

        let displayMessage = '';
        let iconColor = 'text-cyan-400';
        let showPrefix = false;
        let bgColor = 'bg-gradient-to-r from-blue-900/50 to-blue-800/50';
        let textColor = 'text-cyan-300';
        let iconAnimation = '';
        let glowEffect = '';

        if (processStatus === 'creating') {
            displayMessage = `Creating Token${dots}`;
            iconColor = 'text-amber-400';
            bgColor = 'bg-gradient-to-r from-amber-900/50 to-amber-800/50';
            textColor = 'text-amber-300';
            iconAnimation = 'animate-spin';
            glowEffect = 'before:absolute before:inset-0 before:rounded-lg before:bg-amber-500/20 before:blur-sm before:animate-pulse';
        } else if (nextRunTime) {
            displayMessage = countdown || 'Calculating...';
            showPrefix = true;
            glowEffect = 'before:absolute before:inset-0 before:rounded-lg before:bg-cyan-500/20 before:blur-sm';
        }

        return (
            <div className="flex items-center justify-between px-4 py-3 border-b border-blue-500/30 bg-gradient-to-b from-blue-950/30 to-blue-900/30 backdrop-blur-sm">
                <div className="flex items-center gap-3">
                    <div className="relative group">
                        <div className={`relative z-10 p-1 rounded-full ${processStatus === 'creating' ? 'bg-amber-950/50' : 'bg-blue-950/50'}`}>
                            {processStatus === 'creating' ? (
                                <Loader2 className={`w-5 h-5 ${iconColor} animate-spin transition-colors duration-300`} />
                            ) : (
                                <Timer className={`w-5 h-5 ${iconColor} transition-colors duration-300`} />
                            )}
                        </div>
                        <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-full blur animate-pulse group-hover:from-blue-500/30 group-hover:to-cyan-500/30 transition-all duration-300" />
                    </div>
                    {showPrefix && (
                        <span className="text-sm font-medium text-cyan-200 tracking-wide">
                            Next Token Creation:
                        </span>
                    )}
                    <div className={`relative font-mono ${textColor} ${bgColor} px-3 py-1.5 rounded-lg shadow-lg ${glowEffect} transition-all duration-300`}>
                        <span className="relative z-10">{displayMessage}</span>
                    </div>
                </div>
            </div>
        );
    });

    const handleWebSocketMessage = useCallback((event) => {
        try {
            const data = JSON.parse(event.data);
            console.log('Received websocket data:', data);

            if (data.type === 'STATE_UPDATE') {
                setProcessStatus(data.content.processStatus);
                if (data.content.nextRun) {
                    setNextRunTime(new Date(data.content.nextRun));
                }
                return;
            }

            // Handle schedule updates
            if (data.type === 'SCHEDULE_UPDATE') {
                const nextRun = new Date(data.content.nextRun);  // Server sends EST time
                setNextRunTime(nextRun);

                // **Only add 'Preparing systems...' message if the terminal is empty**
                setMessages(prevMessages => {
                    if (prevMessages.length === 0) {
                        return [
                            ...prevMessages,
                            {
                                type: 'AI_RESPONSE',
                                thought: {
                                    result_thought: `Preparing systems for next token creation at ${formatESTTime(nextRun.toISOString())}. Standing by to analyze market trends and identify emerging opportunities.`,
                                    next_thought: null,
                                    command: null
                                },
                                timestamp: new Date().toISOString(),
                                id: Math.random().toString(36).substr(2, 9)
                            }
                        ];
                    }
                    return prevMessages;
                });

                return;
            }

            // Handle system messages about new processes
            if (data.type === 'SYSTEM') {
                if (data.message?.includes('STARTING NEW TOKEN CREATION PROCESS')) {
                    // Clear messages only when the new token creation process starts
                    setMessages([]);
                    setSystemStatus(prev => ({ ...prev, isInitializing: true }));
                    setProcessStatus('creating');
                    return;
                } else if (data.message?.includes('TOKEN CREATED SUCCESSFULLY')) {
                    // Do not clear messages when the token finishes creation
                    setSystemStatus(prev => ({ ...prev, isInitializing: false }));
                    setProcessStatus('complete');
                    return;
                }
            }

            // Create a unique message identifier based on content and timestamp
            const getMessageIdentifier = (msg) => {
                const content = msg.message || msg.content;
                const timestamp = msg.timestamp || '';
                if (typeof content === 'string') {
                    try {
                        const parsed = JSON.parse(content.replace(/```json\s*|\s*```/g, '').trim());
                        return JSON.stringify(parsed) + timestamp;
                    } catch {
                        return content + timestamp;
                    }
                }
                return JSON.stringify(content) + timestamp;
            };

            // Get identifier for the current message
            const messageId = getMessageIdentifier(data);
            console.log('Message identifier:', messageId);

            // Check if the message has already been processed
            if (processedMessages.current.has(messageId)) {
                console.log('Duplicate message detected. Skipping:', messageId);
                return;
            }

            // Add the messageId to the processedMessages set
            processedMessages.current.add(messageId);

            // Cleanup old messages
            if (processedMessages.current.size > 1000) {
                const firstKey = processedMessages.current.values().next().value;
                processedMessages.current.delete(firstKey);
            }

            // Prepare the queueMessage
            let queueMessage = data;

            // Handle COMPLETION messages
            if (data.type === 'COMPLETION') {
                queueMessage = {
                    type: 'COMPLETION',
                    content: data.content,
                    timestamp: data.timestamp || new Date().toISOString()
                };
            }

            // Special handling for LOG messages that might contain JSON
            else if (data.type === 'LOG' && typeof data.message === 'string') {
                try {
                    const cleanJson = data.message.replace(/```json\s*|\s*```/g, '').trim();
                    const messageContent = JSON.parse(cleanJson);

                    // Skip LOG messages that are duplicates of COMPLETION messages
                    if (
                        messageContent.result_thought &&
                        messageContent.next_thought &&
                        messageContent.creation_summary
                    ) {
                        console.log('Skipping duplicate LOG message for COMPLETION');
                        return; // Skip this message
                    }

                    if (messageContent.result_thought || messageContent.next_thought || messageContent.command) {
                        queueMessage = {
                            type: 'AI_RESPONSE',
                            thought: {
                                result_thought: messageContent.result_thought,
                                next_thought: messageContent.next_thought,
                                command: messageContent.command
                            },
                            timestamp: data.timestamp || new Date().toISOString()
                        };
                    }
                } catch (e) {
                    // Not JSON, use message as-is
                }
            }

            // Handle direct JSON messages
            else if (typeof data.message === 'string') {
                try {
                    const parsed = JSON.parse(data.message);
                    if (parsed.result_thought || parsed.next_thought) {
                        queueMessage = {
                            type: 'AI_RESPONSE',
                            thought: {
                                result_thought: parsed.result_thought,
                                next_thought: parsed.next_thought,
                                command: parsed.command
                            },
                            timestamp: data.timestamp || new Date().toISOString()
                        };
                    }
                } catch (e) {
                    // Not JSON or other format, continue with normal processing
                }
            }

            // Add timestamp if not present
            if (!queueMessage.timestamp) {
                queueMessage.timestamp = new Date().toISOString();
            }

            console.log('Final message being queued:', queueMessage);
            messageQueue.current.push(queueMessage);
            processQueue();
        } catch (error) {
            console.error('Error processing incoming message:', error);
            messageQueue.current.push({
                type: 'ERROR',
                message: 'Error processing message from server',
                timestamp: new Date().toISOString()
            });
            processQueue();
        }
    }, [processQueue]);

    // ---------------------------------------------------------------------
    // connectWebSocket: (Re)connect to the server
    // ---------------------------------------------------------------------
    const connectWebSocket = useCallback(() => {
        // Don't try to connect if we already have an active connection
        if (webSocket.current && webSocket.current.readyState === WebSocket.OPEN) {
            return;
        }

        // Close any existing connection first
        if (webSocket.current) {
            webSocket.current.close();
            webSocket.current = null;
        }

        // Initialize WebSocket connection
        webSocket.current = new WebSocket(process.env.NODE_ENV === 'development'
            ? 'ws://localhost:3000'
            : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`);

        webSocket.current.onopen = () => {
            setConnected(true);
            console.log('WebSocket connection established');
            warningDisplayed.current = false;  // Reset warning display status
            // You can also add a message indicating reconnection if desired
        };

        webSocket.current.onclose = () => {
            if (connected && !warningDisplayed.current) {  // Only show warning if not already shown
                setConnected(false);
                messageQueue.current.push({
                    type: 'WARNING',  // Ensure type is uppercase
                    message: 'WARNING: CONNECTION TO KYORA AI CORE LOST',
                    timestamp: new Date().toISOString()
                });
                processQueue();
                warningDisplayed.current = true;  // Mark warning as displayed
            }

            // Attempt to reconnect after a delay
            setTimeout(() => {
                if (!webSocket.current || webSocket.current.readyState === WebSocket.CLOSED) {
                    console.log('Attempting to reconnect WebSocket...');
                    connectWebSocket();
                }
            }, 5000);
        };

        webSocket.current.onmessage = handleWebSocketMessage;

        // After initial connection, subsequent messages will animate
        setTimeout(() => {
            isFirstLoad.current = false;
        }, 1000);
    }, [connected, handleWebSocketMessage, processQueue]);

    // ---------------------------------------------------------------------
    // useEffect: Connect on mount, close on unmount
    // ---------------------------------------------------------------------
    useEffect(() => {
        // Only create a connection if one doesn't exist
        if (!webSocket.current) {
            connectWebSocket();
        }

        // Cleanup function
        return () => {
            if (webSocket.current) {
                webSocket.current.close();
                webSocket.current = null;
            }
        };
    }, [connectWebSocket]); // Added connectWebSocket as dependency

    const RobotAssistantStatus = React.memo(() => {
        return (
            <div className="flex items-center gap-2 px-4 py-2 bg-gray-900/50 rounded-lg border border-blue-500/30">
                <Bot
                    className={`w-6 h-6 ${currentTypingMessage ? 'text-yellow-400 animate-pulse' : 'text-blue-400'
                        }`}
                />
                <div className="flex flex-col">
                    <span className="text-sm text-gray-400">Status:</span>
                    <span className="text-blue-200">
                        {currentTypingMessage ? 'PROCESSING' : 'IDLE'}
                    </span>
                </div>
                {currentTypingMessage && (
                    <span className="text-sm text-gray-400 ml-2 animate-pulse">
                        Processing data...
                    </span>
                )}
            </div>
        );
    });

    // ---------------------------------------------------------------------
    // The main Terminal component layout
    // ---------------------------------------------------------------------
    return (
        <div
            className="w-full overflow-hidden h-full"
            style={{
                backgroundImage: `url('/background.jpg')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed'
            }}
        >            <div className="max-w-6xl mx-auto px-2 sm:px-4 py-2 sm:py-4 h-full flex flex-col">
                {/* Header */}
                <div className="z-40 mb-1 sm:mb-4">
                    <div className="relative border border-blue-500/30 rounded-lg bg-black/90 backdrop-blur-xl p-1 sm:p-6">
                        {/* Decorative elements */}
                        <div className="absolute inset-px bg-gradient-to-b from-blue-500/10 to-transparent" />
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500/0 via-blue-500/50 to-blue-500/0" />
                        <div className="absolute top-0 left-0 w-32 h-1 bg-blue-500/50 animate-pulse" />

                        <div className="relative flex items-stretch justify-between gap-1 sm:gap-8">
                            {/* Logo & Title Section */}
                            <div className="flex items-center gap-1 sm:gap-6">
                                <div className="relative group">
                                    <div className="absolute -inset-1 sm:-inset-2 bg-gradient-to-r from-blue-500/30 to-blue-400/0 rounded-full blur-lg group-hover:from-blue-400/40 transition-all duration-700" />
                                    <div className="relative w-6 h-6 sm:w-12 sm:h-12 rounded-full border-2 border-blue-400/50 overflow-hidden">
                                        <img
                                            src={process.env.PUBLIC_URL + '/kyora.png'}
                                            alt="Kyora AI"
                                            className="w-full h-full object-cover transition-all duration-700"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-blue-500/20 to-transparent" />
                                    </div>
                                </div>
                                <div className="relative">
                                    <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/0 via-blue-500/10 to-blue-500/0 blur" />
                                    <h1 className="text-sm sm:text-3xl font-mono font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-blue-300 to-blue-400">
                                        KYORA AI
                                    </h1>
                                    <div className="flex items-center gap-1 sm:gap-2">
                                        <p className="text-[10px] sm:text-sm font-medium tracking-wider text-blue-200/70">NEURAL v1.0</p>
                                        <div className="w-1 h-1 sm:w-2 sm:h-2 rounded-full bg-blue-400/50 animate-pulse" />
                                    </div>
                                </div>
                            </div>

                            {/* Controls Section */}
                            <div className="flex items-center gap-1 sm:gap-4">
                                {/* Status Indicator */}
                                <div className="relative group">
                                    <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/10 to-blue-400/5 rounded-lg blur-sm transition-all duration-300" />
                                    <div className="relative flex items-center gap-1 sm:gap-3 px-1 sm:px-5 py-0.5 sm:py-3 bg-blue-950/40 rounded-lg border border-blue-400/30">
                                        <div className="flex items-center gap-1 sm:gap-3">
                                            <div className="relative w-5 h-5 sm:w-7 sm:h-7 rounded-full overflow-hidden">
                                                <img
                                                    src={process.env.PUBLIC_URL + '/kyora.png'}
                                                    alt="Kyora"
                                                    className={`w-full h-full object-cover ${currentTypingMessage ? 'opacity-70' : ''} transition-opacity duration-300`}
                                                />
                                                {currentTypingMessage && (
                                                    <div className="absolute inset-0 bg-yellow-400/30 animate-pulse rounded-full" />
                                                )}
                                            </div>
                                            <div className="hidden sm:flex flex-col">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs text-blue-200/70 font-medium tracking-wider">STATUS</span>
                                                    <div className={`w-1.5 h-1.5 rounded-full ${currentTypingMessage ? 'bg-yellow-400 animate-ping' : 'bg-blue-400'}`} />
                                                </div>
                                                <span className="text-sm text-blue-200 font-mono tracking-wide whitespace-nowrap">
                                                    {currentTypingMessage ? 'PROCESSING' : 'IDLE'}
                                                </span>
                                            </div>
                                            {/* Mobile Status */}
                                            <div className="flex sm:hidden items-center">
                                                <div className={`w-1.5 h-1.5 rounded-full ${currentTypingMessage ? 'bg-yellow-400 animate-ping' : 'bg-blue-400'}`} />
                                            </div>
                                        </div>
                                        {currentTypingMessage && (
                                            <div className="hidden sm:block pl-3 border-l border-blue-400/20">
                                                <span className="text-sm text-yellow-400/70 font-mono animate-pulse whitespace-nowrap">
                                                    Processing...
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Separator */}
                                <div className="h-6 sm:h-12 w-px bg-gradient-to-b from-blue-400/30 via-blue-400/10 to-blue-400/30" />

                                {/* Auto-Scroll Toggle */}
                                <div className="relative group">
                                    <div className="absolute -inset-1 bg-gradient-to-r from-blue-400/5 to-blue-500/10 rounded-lg blur-sm transition-all duration-300" />
                                    <div
                                        className="relative flex items-center gap-1 sm:gap-4 px-1 sm:px-4 py-0.5 sm:py-3 bg-blue-950/40 rounded-lg border border-blue-400/30 hover:border-blue-400/50 transition-all duration-300 cursor-pointer select-none"
                                        onClick={() => setAutoScroll(prev => !prev)}
                                    >
                                        <span className="hidden sm:inline text-xs text-blue-200/70 font-medium tracking-wider">AUTO-SCROLL</span>
                                        <div className="flex items-center gap-1 sm:gap-3">
                                            <div className={`relative w-7 sm:w-10 h-4 sm:h-5 rounded-full transition-colors duration-300 ${autoScroll ? 'bg-blue-500' : 'bg-gray-700'}`}>
                                                <div className={`absolute top-0.5 left-0.5 w-3 sm:w-4 h-3 sm:h-4 rounded-full transition-all duration-300 
                                    ${autoScroll ? 'translate-x-3 sm:translate-x-5 bg-white shadow-lg' : 'translate-x-0 bg-gray-400'}`} />
                                            </div>
                                            <span className={`text-[10px] sm:text-xs font-mono tracking-wide whitespace-nowrap transition-colors duration-300
                                ${autoScroll ? 'text-blue-200' : 'text-blue-200/50'}`}>
                                                {autoScroll ? 'ON' : 'OFF'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Split Terminal Layout */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 flex-1 overflow-hidden">
                    {/* Main Terminal */}
                    <div className="relative border border-blue-500/30 rounded-lg bg-black/90 flex flex-col overflow-hidden flex-1">
                        <CountdownDisplay />

                        {/* Terminal Header */}
                        <div className="relative px-4 py-3 border-b border-blue-500/30 bg-blue-950/40 mb-1 sm:mb-0">
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500/0 via-blue-500/20 to-blue-500/0" />
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <TerminalIcon className="w-4 h-4 text-blue-400" />
                                    <span className="text-sm font-mono text-blue-200">MAIN TERMINAL</span>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-2">
                                        <Binary className="w-4 h-4 text-blue-400/70" />
                                        <span className="text-xs font-mono text-blue-200/70">CORE/0x01</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Network className="w-4 h-4 text-blue-400/70" />
                                        <span className="text-xs font-mono text-blue-200/70">NODE/ACTIVE</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Terminal Content */}
                        <div className="flex-1 overflow-hidden relative">
                            <div className="absolute top-0 left-0 w-full h-6 bg-gradient-to-b from-black to-transparent z-10 pointer-events-none" />

                            <div
                                ref={terminalRef}
                                className="h-full overflow-y-auto overflow-x-hidden font-mono text-sm px-4 py-3 scroll-smooth"
                                style={{
                                    scrollbarWidth: 'thin',
                                    scrollbarColor: '#3b82f6 #1e293b'
                                }}
                            >
                                {/* Compute pumpfunUrl to pass to MessageComponent */}
                                {messages.map((msg, idx) => (
                                    <MessageComponent
                                        key={msg.id || idx}
                                        message={msg}
                                        showApiOnly={false}
                                        pumpfunUrl={messages.find(m => m.type === 'API_RESPONSE' && m.content?.data?.launch_url)?.content?.data?.launch_url || null}
                                        formatESTTime={formatESTTime}
                                    />
                                ))}
                                {currentTypingMessage && (
                                    <MessageComponent
                                        message={currentTypingMessage}
                                        showApiOnly={false}
                                        pumpfunUrl={messages.find(m => m.type === 'API_RESPONSE' && m.content?.data?.launch_url)?.content?.data?.launch_url || null}
                                        formatESTTime={formatESTTime}
                                    />
                                )}
                            </div>

                            <div className="absolute bottom-0 left-0 w-full h-6 bg-gradient-to-t from-black to-transparent z-10 pointer-events-none" />
                        </div>
                    </div>

                    {/* API Response Terminal */}
                    <div className="relative border border-blue-500/30 rounded-lg bg-black/90 flex flex-col overflow-hidden flex-1">
                        {/* API Terminal Header */}
                        <div className="relative px-4 py-3 border-b border-blue-500/30 bg-blue-950/40">
                            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500/0 via-blue-500/20 to-blue-500/0" />
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <Braces className="w-4 h-4 text-green-400" />
                                    <span className="text-sm font-mono text-green-300">API RESPONSES</span>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-2">
                                        <Waypoints className="w-4 h-4 text-green-400/70" />
                                        <span className="text-xs font-mono text-green-300/70">API/ACTIVE</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Fan className="w-4 h-4 text-green-400/70 animate-spin-slow" />
                                        <span className="text-xs font-mono text-green-300/70">SYSTEM/OK</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* API Terminal Content */}
                        <div className="flex-1 overflow-hidden relative">
                            <div className="absolute top-0 left-0 w-full h-6 bg-gradient-to-b from-black to-transparent z-10 pointer-events-none" />

                            <div
                                className="h-full overflow-y-auto overflow-x-hidden font-mono text-sm px-4 py-3 scroll-smooth"
                                style={{
                                    scrollbarWidth: 'thin',
                                    scrollbarColor: '#3b82f6 #1e293b'
                                }}
                            >
                                <div className="space-y-4">
                                    {messages.map((msg, idx) => {
                                        if (msg.type === 'API_RESPONSE') {
                                            return (
                                                <div key={`api-${msg.id || idx}`}
                                                    className="rounded-lg border border-green-500/20 bg-green-950/10 p-4">
                                                    {msg.command && (
                                                        <>
                                                            <div className="text-blue-200 mb-1 font-semibold">Request:</div>
                                                            <div className="overflow-hidden">
                                                                <div className="text-yellow-300 bg-yellow-900/20 rounded p-2 font-mono whitespace-pre overflow-x-auto">
                                                                    {typeof msg.command === 'string'
                                                                        ? msg.command
                                                                        : JSON.stringify(msg.command, null, 2)}
                                                                </div>
                                                            </div>
                                                        </>
                                                    )}
                                                    <div className="text-blue-200 mb-1 font-semibold mt-4">Response:</div>
                                                    <div className="overflow-hidden">
                                                        <div className="text-green-300 bg-green-900/20 rounded p-2 font-mono whitespace-pre overflow-x-auto">
                                                            {typeof msg.content === 'string'
                                                                ? msg.content
                                                                : JSON.stringify(msg.content, null, 2)}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }
                                        return null;
                                    })}
                                </div>
                            </div>

                            <div className="absolute bottom-0 left-0 w-full h-6 bg-gradient-to-t from-black to-transparent z-10 pointer-events-none" />
                        </div>

                    </div>
                </div>
            </div>

            {/* Custom Scrollbar Styles */}
            <style jsx global>{`
                /* Webkit Scrollbar Styles */
                ::-webkit-scrollbar {
                    width: 8px;
                    height: 8px;
                }

                ::-webkit-scrollbar-track {
                    background: #1e293b;
                    border-radius: 4px;
                }

                ::-webkit-scrollbar-thumb {
                    background: #3b82f6;
                    border-radius: 4px;
                }

                ::-webkit-scrollbar-thumb:hover {
                    background: #60a5fa;
                }

                /* Firefox Scrollbar Styles */
                * {
                    scrollbar-width: thin;
                    scrollbar-color: #3b82f6 #1e293b;
                }

                /* Add animation for spin-slow */
                @keyframes spin-slow {
                    from {
                        transform: rotate(0deg);
                    }
                    to {
                        transform: rotate(360deg);
                    }
                }
                
                .animate-spin-slow {
                    animation: spin-slow 3s linear infinite;
                }

                 /* Hide scrollbar for Chrome, Safari and Opera */
                .overflow-x-auto::-webkit-scrollbar {
                    height: 6px;
                }

                .overflow-x-auto::-webkit-scrollbar-track {
                    background: rgba(30, 41, 59, 0.5);
                    border-radius: 3px;
                }

                .overflow-x-auto::-webkit-scrollbar-thumb {
                    background: rgba(59, 130, 246, 0.5);
                    border-radius: 3px;
                }

                .overflow-x-auto::-webkit-scrollbar-thumb:hover {
                    background: rgba(96, 165, 250, 0.7);
                }
            `}</style>
        </div>
    );
};

export default Terminal;
